import React from 'react'
import { formatVehicleDisplayTitle } from './StockVehiclePanel'
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts'
import { formatRecentDropLine, hasRecentPriceDropChf } from '../utils/recentPriceDrop'
import { applyClusterJitter } from '../utils/scatterJitter'
import { getAnnonceCompletenessIssues, describeIncompleteAnnonce } from '../utils/incompleteListing'
import { useBackToClose } from '../hooks/useBackToClose'

const VW_BLUE = '#001E50'
const RADIUS_BLOCK = 6
const RADIUS_PILL = 4

function situationBadgeLarge(situation) {
  const map = {
    critique: { label: 'Critique', bg: 'rgba(127,29,29,0.35)', color: '#fecaca', border: '1px solid rgba(248,113,113,0.45)' },
    expose: { label: 'Exposé', bg: 'rgba(163,45,45,0.12)', color: '#e7c08f', border: '1px solid rgba(224,184,120,0.35)' },
    sature: { label: 'Saturé', bg: 'rgba(255,255,255,0.06)', color: 'rgba(226,232,240,0.85)', border: '1px solid rgba(148,163,184,0.22)' },
    dans_le_marche: { label: 'Marché', bg: 'rgba(255,255,255,0.06)', color: 'rgba(226,232,240,0.75)', border: '1px solid rgba(255,255,255,0.12)' },
    avantage: { label: 'Avantage', bg: 'rgba(22,101,52,0.35)', color: '#bbf7d0', border: '1px solid rgba(74,222,128,0.45)' },
    exclusivite: { label: 'Exclusivité', bg: 'rgba(0,30,80,0.28)', color: '#bfdbfe', border: '1px solid rgba(0,30,80,0.5)' },
  }
  return map[situation] || map.dans_le_marche
}

function situationConcurrenceLibelle(situation) {
  const map = {
    exclusivite: 'Peu d’annonces équivalentes localement — visibilité favorable.',
    critique: 'Nombreuses annonces concurrentes sur ce créneau prix.',
    expose: 'Prix au-dessus de la médiane concurrente — exposition au filtre prix.',
    sature: 'Offre dense sur ce modèle — différenciation utile.',
    dans_le_marche: 'Concurrence équilibrée sur ce modèle.',
    avantage: 'Prix inférieur à la médiane des annonces comparables.',
  }
  return map[situation] || map.dans_le_marche
}

function titreCourt(t) {
  const s = String(t || '').trim()
  return s.length > 48 ? `${s.slice(0, 47)}…` : s
}

function cantonAffiche(code) {
  const s = String(code || '').trim().toUpperCase()
  return s.length ? s : null
}

/**
 * Texte périmètre concurrentiel (aligné sur source_geo / rayon_km du backend).
 */
function concurrencePerimetreCopy(sourceGeo, rayonKm, cantonMoto) {
  const cy = cantonAffiche(cantonMoto)
  const r = Number(rayonKm)
  const rk = Number.isFinite(r) && r > 0 ? Math.round(r) : 120
  if (sourceGeo === 'canton') {
    return {
      body: cy
        ? `Les annonces ci-dessous sont des **professionnels dans le canton ${cy}** (même canton que votre annonce). La médiane et l’échantillon ne portent que sur ce périmètre.`
        : `Les annonces ci-dessous sont des **professionnels dans le même canton** que votre annonce. La médiane et l’échantillon ne portent que sur ce périmètre.`,
    }
  }
  if (sourceGeo === 'rayon') {
    return {
      body: cy
        ? `Pas d’annonce comparable dans le canton **${cy}** : les concurrents affichés sont des pros dans un **rayon d’environ ${rk} km** autour de ce canton (distance entre centroïdes de cantons, puis annonces les plus proches du modèle). **La médiane et la liste = uniquement ce périmètre.**`
        : `Les concurrents affichés sont des pros dans un **rayon d’environ ${rk} km** (centroïdes de cantons). **La médiane et la liste = ce périmètre uniquement.**`,
    }
  }
  return {
    body: cy
      ? `**Aucune annonce** dans le canton **${cy}** ni dans le rayon de **${rk} km** : la comparaison s’élargit à la **Suisse entière** pour garder une médiane exploitable. Les villes listées peuvent être éloignées.`
      : `Périmètre élargi à la **Suisse entière** (canton ou rayon insuffisant pour isoler des concurrents).`,
  }
}

function PerimetreBodyRich({ text }) {
  const parts = String(text).split(/\*\*(.+?)\*\*/g)
  return (
    <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
      {parts.map((chunk, i) => (i % 2 === 1 ? <strong key={i} style={{ color: 'var(--text)', fontWeight: 600 }}>{chunk}</strong> : <span key={i}>{chunk}</span>))}
    </p>
  )
}

function SectionRule() {
  return <div style={{ height: 1, background: 'rgba(148,163,184,0.14)', margin: '18px 0' }} />
}

function ecartPillStyle(pct) {
  if (!Number.isFinite(pct)) return { color: 'var(--text-secondary)', border: '1px solid rgba(148,163,184,0.28)', bg: 'rgba(255,255,255,0.02)' }
  if (pct > 8) return { color: '#e7a39a', border: '1px solid rgba(248,113,113,0.35)', bg: 'rgba(248,113,113,0.06)' }
  if (pct < -8) return { color: '#9ec5f7', border: '1px solid rgba(96,165,250,0.35)', bg: 'rgba(96,165,250,0.06)' }
  return { color: 'var(--text-secondary)', border: '1px solid rgba(148,163,184,0.28)', bg: 'rgba(255,255,255,0.02)' }
}

function formatHistDate(iso) {
  if (!iso) return null
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  return new Date(t).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatPriceChainCompact(history) {
  if (!Array.isArray(history) || history.length === 0) return null
  const values = []
  for (const step of history) {
    const oldP = Number(step?.ancien_prix)
    const newP = Number(step?.nouveau_prix)
    if (Number.isFinite(oldP) && values.length === 0) values.push(oldP)
    if (Number.isFinite(newP)) values.push(newP)
  }
  const compact = values.filter((v, i) => i === 0 || v !== values[i - 1]).map((v) => `${Number(v).toLocaleString('fr-CH')} CHF`)
  if (compact.length < 2) return null
  if (compact.length <= 3) return compact.join(' à ')
  return `${compact[0]} … ${compact[compact.length - 1]}`
}

function getHistorySteps(history) {
  if (!Array.isArray(history) || history.length === 0) return []
  return history.map((step, idx) => {
    const ap = Number(step?.ancien_prix)
    const np = Number(step?.nouveau_prix)
    const dRaw = Number(step?.delta)
    const d = Number.isFinite(dRaw) ? dRaw : Number.isFinite(ap) && Number.isFinite(np) ? np - ap : NaN
    return {
      n: idx + 1,
      from: Number.isFinite(ap) ? `${ap.toLocaleString('fr-CH')} CHF` : '…',
      to: Number.isFinite(np) ? `${np.toLocaleString('fr-CH')} CHF` : '…',
      delta: Number.isFinite(d) ? `${d > 0 ? '+' : ''}${d.toLocaleString('fr-CH')} CHF` : '…',
      isDrop: Number.isFinite(d) && d < 0,
      dateLabel: formatHistDate(step?.cree_at),
    }
  })
}

function scatterDomains(points, you) {
  const kms = [...points.map((p) => Number(p.km)), ...(you ? [Number(you.km)] : [])].filter((v) => Number.isFinite(v))
  const prix = [...points.map((p) => Number(p.prix)), ...(you ? [Number(you.prix)] : [])].filter((v) => Number.isFinite(v))
  if (!kms.length || !prix.length) return null
  const kmMinRaw = Math.min(...kms)
  const kmMaxRaw = Math.max(...kms)
  const pMinRaw = Math.min(...prix)
  const pMaxRaw = Math.max(...prix)

  const kmSpan = Math.max(1, kmMaxRaw - kmMinRaw)
  const pSpan = Math.max(1, pMaxRaw - pMinRaw)

  const kmStepCandidates = [100, 200, 500, 1000, 2000, 5000, 10000]
  const pStepCandidates = [100, 200, 500, 1000, 2000, 5000, 10000]
  const kmStep = kmStepCandidates.find((s) => kmSpan / s <= 6) || kmStepCandidates[kmStepCandidates.length - 1]
  const pStep = pStepCandidates.find((s) => pSpan / s <= 6) || pStepCandidates[pStepCandidates.length - 1]

  const padKm = Math.max(kmStep, kmSpan * 0.12)
  const padP = Math.max(pStep, pSpan * 0.1)
  const kmMin = Math.max(0, Math.floor((kmMinRaw - padKm) / kmStep) * kmStep)
  const kmMax = Math.ceil((kmMaxRaw + padKm) / kmStep) * kmStep
  const pMin = Math.max(0, Math.floor((pMinRaw - padP) / pStep) * pStep)
  const pMax = Math.ceil((pMaxRaw + padP) / pStep) * pStep
  return {
    kmMin,
    kmMax: Math.max(kmMin + kmStep, kmMax),
    prixMin: pMin,
    prixMax: Math.max(pMin + pStep, pMax),
  }
}

function normalizeYear(value) {
  const y = parseInt(String(value ?? ''), 10)
  return Number.isFinite(y) ? y : null
}

/**
 * Panneau latéral détail concurrence (même gabarit visuel que le détail stock).
 */
export function ConcurrenceDetailPanel({
  row,
  coterStock,
  onClose,
  onCoterMoto,
  onOpenMonAnnonce,
  onOpenFicheConcurrent,
  onOpenReglages,
}) {
  // Bouton retour système (mobile) → ferme le panneau au lieu de quitter l'app.
  useBackToClose(Boolean(row), onClose)
  const mm = row?.ma_moto
  const c = row?.concurrence
  if (!mm || !c) return null

  const completeness = getAnnonceCompletenessIssues(mm)
  const isIncomplete = completeness.incomplete

  const st = situationBadgeLarge(c.situation)
  const ecartChf = Number(c.ecart_vs_median_chf)
  const ecartSuffix =
    (c.situation === 'expose' || c.situation === 'critique') && Number.isFinite(ecartChf)
      ? ` · ${ecartChf > 0 ? '+' : ''}${ecartChf.toLocaleString('fr-CH')} CHF vs médiane`
      : ''
  const liste = Array.isArray(c.tous_les_concurrents) ? c.tous_les_concurrents.slice(0, 5) : []
  const moinsCher = c.concurrent_le_moins_cher || null
  const ficheSeller = c.concurrent_le_moins_cher?.seller_id ?? liste[0]?.seller_id
  const ecartPct = Number(c.ecart_vs_median_pct)
  const pillSt = ecartPillStyle(ecartPct)
  const sourceGeo = c.source_geo === 'canton' || c.source_geo === 'rayon' || c.source_geo === 'suisse' ? c.source_geo : 'rayon'
  const nbConc = Number(c.nb_concurrents) || 0
  const perimetre = concurrencePerimetreCopy(sourceGeo, c.rayon_km, mm.canton)
  const afficherTipPeuRayon = sourceGeo === 'rayon' && nbConc >= 1 && nbConc < 3
  const afficherTipSuisse = sourceGeo === 'suisse'
  const montrerBoutonReglages =
    Boolean(onOpenReglages) && (afficherTipSuisse || (sourceGeo === 'rayon' && nbConc >= 1 && nbConc < 3))

  const subtitleParts = [
    mm.first_registration_year,
    mm.km != null ? `${Number(mm.km).toLocaleString('fr-CH')} km` : null,
    mm.model ? String(mm.model).trim() : null,
    [mm.canton, mm.ville].filter(Boolean).join(' · ') || null,
  ].filter(Boolean)
  const modelTitle = formatVehicleDisplayTitle(mm.titre || `${mm.make || ''} ${mm.model || ''}`.trim())
  const modelHoverDetails = [modelTitle, ...subtitleParts, mm.version ? `Version ${mm.version}` : null].filter(Boolean).join(' · ')
  const hasHistoryChain = Array.isArray(mm.price_history) && mm.price_history.length > 1 && !!formatPriceChainCompact(mm.price_history)
  const historySteps = hasHistoryChain ? getHistorySteps(mm.price_history) : []
  const concurrentsRayon = liste
    .map((co) => ({
      km: Number(co?.km),
      prix: Number(co?.prix_chf),
      annee: co?.first_registration_year ?? null,
      titre: co?.titre || 'Annonce concurrente',
      ville: co?.ville || null,
      canton: co?.canton || null,
      image_url: co?.image_url || co?.image_principale_url || null,
      url: co?.url || null,
      baisse_recente_chf:
        Number.isFinite(Number(co?.baisse_recente_chf)) && Number(co.baisse_recente_chf) > 0
          ? Number(co.baisse_recente_chf)
          : null,
    }))
    .filter((p) => Number.isFinite(p.km) && Number.isFinite(p.prix))
  const referenceYear = normalizeYear(mm?.first_registration_year ?? mm?.annee)
  const yourPointBase =
    Number.isFinite(Number(mm?.km)) && Number.isFinite(Number(mm?.prix_chf))
      ? {
          km: Number(mm.km),
          prix: Number(mm.prix_chf),
          annee: mm?.first_registration_year ?? null,
          titre: modelTitle || 'Votre annonce',
          ville: mm?.ville || null,
          canton: mm?.canton || null,
          image_url: mm?.image_principale_url || mm?.image_url || null,
          url: mm?.url || null,
          baisse_recente_chf:
            Number.isFinite(Number(mm?.baisse_recente_chf)) && Number(mm.baisse_recente_chf) > 0
              ? Number(mm.baisse_recente_chf)
              : null,
        }
      : null
  const scDomains = scatterDomains(concurrentsRayon, yourPointBase)
  // On applique le jitter sur l'union (concurrents + votre annonce) pour éviter
  // que des points superposés se masquent, peu importe la série dans laquelle
  // ils sont rendus. `kmReal`/`prixReal` sont conservés pour le tooltip.
  const _allPointsForJitter = scDomains
    ? applyClusterJitter(
        [...concurrentsRayon, ...(yourPointBase ? [{ ...yourPointBase, _isYou: true }] : [])],
        scDomains,
      )
    : [...concurrentsRayon, ...(yourPointBase ? [{ ...yourPointBase, _isYou: true }] : [])]
  const concurrentsRayonJittered = _allPointsForJitter.filter((p) => !p._isYou)
  const yourPoint = _allPointsForJitter.find((p) => p._isYou) || null
  const concurrentsMemeAnnee = concurrentsRayonJittered.filter((p) => {
    const y = normalizeYear(p?.annee)
    return referenceYear != null && y != null && y === referenceYear
  })
  const concurrentsAutreAnnee = concurrentsRayonJittered.filter((p) => {
    const y = normalizeYear(p?.annee)
    return referenceYear == null || y == null || y !== referenceYear
  })
  const ScatterTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const d = payload[0]?.payload
    if (!d) return null
    const dropLine = formatRecentDropLine(d.baisse_recente_chf)
    return (
      <div
        style={{
          background: 'var(--surface-alt)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '8px 10px',
          fontSize: 12,
          maxWidth: 300,
        }}
      >
        {d.image_url ? (
          <img
            src={d.image_url}
            alt=""
            style={{ width: '100%', maxWidth: 280, height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 6 }}
          />
        ) : null}
        <div style={{ color: '#e2e8f0', fontWeight: 600, marginBottom: 4 }}>{d.titre || 'Annonce'}</div>
        <div style={{ color: '#f8fafc', fontWeight: 600 }}>
          {Number(d.prixReal != null ? d.prixReal : d.prix).toLocaleString('fr-CH')} CHF
        </div>
        {dropLine ? (
          <div style={{ color: '#fecaca', fontSize: 11, fontWeight: 600, marginTop: 4 }}>{dropLine}</div>
        ) : null}
        <div style={{ color: 'var(--text-tertiary)' }}>
          {Number(d.kmReal != null ? d.kmReal : d.km).toLocaleString('fr-CH')} km
          {d.annee ? ` · ${d.annee}` : ''}
          {d.isYou ? ' · Votre annonce' : ''}
        </div>
        {(d.ville || d.canton) && <div style={{ color: 'var(--text-tertiary)' }}>{[d.ville, d.canton].filter(Boolean).join(' · ')}</div>}
        {d.url ? (
          <div style={{ color: 'rgba(226,232,240,0.55)', fontSize: 10, marginTop: 3 }}>
            Double-cliquez pour ouvrir l&apos;annonce
          </div>
        ) : null}
      </div>
    )
  }

  const ScatterPointShape = ({ cx, cy, payload, fill, stroke, strokeWidth, r = 5 }) => {
    const recentDrop = hasRecentPriceDropChf(payload?.baisse_recente_chf)
    return (
    <g
      onDoubleClick={(e) => {
        e.stopPropagation()
        if (payload?.url) window.open(payload.url, '_blank', 'noopener,noreferrer')
      }}
      style={{ cursor: payload?.url ? 'pointer' : 'default' }}
    >
      {recentDrop ? (
        <circle
          cx={cx}
          cy={cy}
          r={r + 3}
          fill="none"
          stroke="rgba(248,113,113,0.72)"
          strokeWidth={2}
          pointerEvents="none"
        />
      ) : null}
      <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </g>
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Détail concurrence"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4500,
        background: 'rgba(10,12,14,0.55)',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'stretch',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: '100vw',
          height: '100%',
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            padding: '16px 18px 12px',
            borderBottom: '1px solid rgba(148,163,184,0.14)',
            background: 'var(--surface)',
            zIndex: 3,
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, width: 80 }}>
              {mm.image_principale_url ? (
                <img
                  src={mm.image_principale_url}
                  alt=""
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: RADIUS_BLOCK, background: 'var(--surface-alt)' }}
                />
              ) : (
                <div style={{ width: 80, height: 80, borderRadius: RADIUS_BLOCK, background: 'var(--surface-alt)', border: '1px solid var(--border)' }} />
              )}
              {mm.url ? (
                <a
                  href={mm.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', marginTop: 6, fontSize: 12, fontWeight: 600, color: '#93c5fd', textDecoration: 'none' }}
                >
                  Ouvrir l&apos;annonce
                </a>
              ) : null}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingRight: 36, position: 'relative' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, lineHeight: 1.25 }}>
                {mm.url ? (
                  <a
                    href={mm.url}
                    target="_blank"
                    rel="noreferrer"
                    title={modelHoverDetails}
                    style={{ color: 'var(--text)', textDecoration: 'none', cursor: 'pointer' }}
                  >
                    {modelTitle}
                  </a>
                ) : (
                  <span style={{ color: 'var(--text)' }} title={modelHoverDetails}>
                    {modelTitle}
                  </span>
                )}
              </h2>
              {subtitleParts.length ? (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{subtitleParts.join(' · ')}</p>
              ) : null}
              <button
                type="button"
                aria-label="Fermer"
                onClick={onClose}
                className="drawer-close-btn"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  borderRadius: RADIUS_BLOCK,
                  border: '1px solid var(--border)',
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--text)',
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 18px 12px' }}>
          {isIncomplete ? (
            <div
              style={{
                marginTop: 16,
                padding: '14px 16px',
                borderRadius: RADIUS_BLOCK,
                border: '1px solid rgba(220,38,38,0.55)',
                background: 'rgba(220,38,38,0.10)',
                color: '#fecaca',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span
                  aria-hidden
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: 'rgba(220,38,38,0.35)',
                    color: '#fee2e2',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  !
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.01em', color: '#fee2e2' }}>
                  Annonce incomplète
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55, color: '#fecaca' }}>
                {describeIncompleteAnnonce(completeness)}
              </p>
              <p style={{ margin: '10px 0 0', fontSize: 11, lineHeight: 1.5, color: 'rgba(254,202,202,0.75)' }}>
                Tant que la fiche reste vide, aucune comparaison concurrentielle n&apos;est possible : la « médiane » et le périmètre affichent 0 par construction. Ce n&apos;est <strong style={{ color: '#fee2e2' }}>pas</strong> une situation d&apos;exclusivité.
              </p>
              {mm.url ? (
                <a
                  href={mm.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: 12,
                    padding: '8px 12px',
                    borderRadius: RADIUS_PILL,
                    border: '1px solid rgba(220,38,38,0.55)',
                    background: 'rgba(220,38,38,0.18)',
                    color: '#fee2e2',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                  }}
                >
                  Ouvrir l&apos;annonce sur MotoScout
                </a>
              ) : null}
            </div>
          ) : null}
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em' }}>
              {mm.prix_chf != null ? `${Number(mm.prix_chf).toLocaleString('fr-CH')} CHF` : '—'}
            </div>
            {!isIncomplete && Number.isFinite(ecartPct) ? (
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 8,
                  padding: '4px 10px',
                  borderRadius: RADIUS_PILL,
                  fontSize: 12,
                  fontWeight: 500,
                  ...pillSt,
                }}
              >
                {ecartPct > 0 ? '+' : ''}
                {ecartPct}% vs médiane concurrents
              </span>
            ) : null}
          </div>

          {!isIncomplete && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
                marginTop: 18,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                    marginBottom: 4,
                  }}
                >
                  Concurrents actifs
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{c.nb_concurrents ?? 0}</div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                    marginBottom: 4,
                  }}
                >
                  Médiane concurrents
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
                  {c.prix_median_concurrent != null ? `${Number(c.prix_median_concurrent).toLocaleString('fr-CH')} CHF` : '—'}
                </div>
              </div>
            </div>
          )}

          {!isIncomplete && (
          <div
            style={{
              marginTop: 16,
              padding: '12px 14px',
              borderRadius: RADIUS_BLOCK,
              border: '1px solid var(--border)',
              background: 'rgba(255,255,255,0.03)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--text-tertiary)',
                marginBottom: 8,
                fontVariant: 'small-caps',
              }}
            >
              Périmètre de comparaison
            </div>
            <PerimetreBodyRich text={perimetre.body} />
            {afficherTipPeuRayon ? (
              <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                Peu d’annonces dans ce rayon — vous pouvez augmenter le rayon de recherche dans les réglages pour tenter d’en rassembler davantage.
              </p>
            ) : null}
            {afficherTipSuisse ? (
              <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                Pour privilégier une zone plus proche lorsque c’est possible, augmentez le rayon (Réglages ▸ Intelligence concurrentielle).
              </p>
            ) : null}
            {montrerBoutonReglages ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenReglages()
                }}
                style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  borderRadius: RADIUS_PILL,
                  border: '1px solid rgba(0,30,80,0.45)',
                  background: 'rgba(0,30,80,0.18)',
                  color: '#bfdbfe',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Ouvrir les réglages
              </button>
            ) : null}
          </div>
          )}

          {!isIncomplete && <SectionRule />}

          {!isIncomplete && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: RADIUS_PILL,
                fontSize: 11,
                fontWeight: 600,
                background: st.bg,
                color: st.color,
                border: st.border,
              }}
            >
              {st.label}
              {ecartSuffix}
            </span>
          </div>
          )}
          {!isIncomplete && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{situationConcurrenceLibelle(c.situation)}</p>
          )}

          {isIncomplete ? null : c.situation === 'exclusivite' ? (
            <>
              <SectionRule />
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                Aucun concurrent professionnel sur ce modèle dans le périmètre décrit ci-dessus (canton, puis rayon, puis repli national si besoin). Position de force — pas besoin de baisser le prix.
              </p>
              {onOpenReglages ? (
                <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                  Si vous souhaitez vérifier une zone plus large par curiosité, vous pouvez augmenter le rayon dans les réglages ; cela ne changera rien tant qu’aucune annonce comparable n’existe.
                </p>
              ) : null}
            </>
          ) : (
            <>
              <SectionRule />
              {moinsCher ? (
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--text-tertiary)',
                      marginBottom: 8,
                    }}
                  >
                    Concurrent le moins cher
                  </div>
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: RADIUS_BLOCK,
                      border: '1px solid rgba(148,163,184,0.2)',
                      background: 'rgba(255,255,255,0.02)',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{titreCourt(moinsCher.titre) || 'Annonce'}</span>
                    <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
                      {moinsCher.prix_chf != null ? `${Number(moinsCher.prix_chf).toLocaleString('fr-CH')} CHF` : '—'}
                    </span>
                    {moinsCher.url ? (
                      <a href={moinsCher.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#93c5fd' }}>
                        Ouvrir l&apos;annonce
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                  marginBottom: 8,
                }}
              >
                {`Échantillon annonces (${c.nb_concurrents || 0} retenue${(c.nb_concurrents || 0) !== 1 ? 's' : ''} pour la médiane — périmètre ci-dessus)`}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.75fr 0.55fr 0.5fr 0.45fr',
                  gap: 8,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                  padding: '6px 8px',
                  borderBottom: '1px solid rgba(148,163,184,0.18)',
                }}
              >
                <span>Annonce</span>
                <span style={{ textAlign: 'right' }}>Prix</span>
                <span>Année</span>
                <span>Km</span>
                <span style={{ textAlign: 'right' }}>Lien</span>
              </div>
              <div style={{ marginBottom: 14 }}>
                {liste.map((co, j) => (
                  <div
                    key={`${co.url}-${j}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 0.75fr 0.55fr 0.5fr 0.45fr',
                      gap: 8,
                      alignItems: 'center',
                      minHeight: 36,
                      padding: '0 8px',
                      fontSize: 12,
                      color: 'var(--text)',
                      borderBottom: '1px solid rgba(148,163,184,0.1)',
                    }}
                  >
                    <span style={{ fontWeight: 500, minWidth: 0 }}>
                      {titreCourt(co.titre)}
                      {co.ville || co.canton ? (
                        <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}> ({[co.ville, co.canton].filter(Boolean).join(' · ')})</span>
                      ) : null}
                    </span>
                    <span style={{ textAlign: 'right', fontWeight: 500 }}>
                      {co.prix_chf != null ? `${Number(co.prix_chf).toLocaleString('fr-CH')} CHF` : '—'}
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}>{co.first_registration_year ?? '—'}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{co.km != null ? `${Number(co.km).toLocaleString('fr-CH')}` : '—'}</span>
                    <span style={{ textAlign: 'right' }}>
                      {co.url ? (
                        <a href={co.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 600, color: '#93c5fd' }}>
                          Voir
                        </a>
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {hasHistoryChain ? (
            <>
              <SectionRule />
              <div
                style={{
                  marginTop: 4,
                  padding: '12px 14px',
                  borderRadius: RADIUS_BLOCK,
                  border: '1px solid rgba(248,113,113,0.28)',
                  background: 'rgba(248,113,113,0.06)',
                }}
              >
                <div style={{ fontSize: 10, color: '#fecaca', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 6 }}>
                  Synthèse historique des prix
                </div>
                <div style={{ fontSize: 12, color: '#fca5a5', lineHeight: 1.45 }}>{formatPriceChainCompact(mm.price_history)}</div>
                {historySteps.length > 0 ? (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(248,113,113,0.22)' }}>
                    {historySteps.map((row) => (
                      <div key={row.n} style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                        <div>
                          <span style={{ color: '#94a3b8', fontWeight: 600 }}>{row.n}.</span>{' '}
                          <span style={{ color: 'var(--text)' }}>
                            {row.from} → {row.to}
                          </span>{' '}
                          <span style={{ fontWeight: 600, color: row.isDrop ? '#f87171' : '#94a3b8' }}>({row.delta})</span>
                        </div>
                        {row.dateLabel ? (
                          <div style={{ marginTop: 4, marginLeft: 14, fontSize: 11, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(148,163,184,0.9)' }}>
                            {row.dateLabel}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}

          {!isIncomplete && scDomains && concurrentsRayon.length > 0 ? (
            <>
              <SectionRule />
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: RADIUS_BLOCK, padding: '12px 12px 10px' }}>
                <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6 }}>
                  Prix vs kilométrage (périmètre concurrence)
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10, lineHeight: 1.45 }}>
                  Nuage limité aux annonces concurrentes du périmètre ci-dessus ({sourceGeo === 'rayon' ? 'rayon' : sourceGeo === 'canton' ? 'canton' : 'Suisse'}). Halo rouge : baisse de prix sur 14 jours.
                  <span style={{ display: 'block', marginTop: 4 }}>
                    Blanc = même année · Gris = autre année · Bleu = votre annonce
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={230}>
                  <ScatterChart margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="rgba(226,232,240,0.08)" />
                    <XAxis
                      type="number"
                      dataKey="km"
                      domain={[scDomains.kmMin, scDomains.kmMax]}
                      tick={{ fill: 'rgba(226,232,240,0.38)', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      tickCount={6}
                      allowDecimals={false}
                      tickFormatter={(v) => {
                        const n = Number(v)
                        if (!Number.isFinite(n)) return ''
                        if (n < 10000) return `${Math.round(n).toLocaleString('fr-CH')}`
                        return `${Math.round(n / 1000)}k`
                      }}
                      label={{ value: 'km', position: 'insideBottom', offset: -2, fill: 'rgba(226,232,240,0.32)', fontSize: 10 }}
                    />
                    <YAxis
                      type="number"
                      dataKey="prix"
                      domain={[scDomains.prixMin, scDomains.prixMax]}
                      tick={{ fill: 'rgba(226,232,240,0.38)', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={52}
                      tickCount={6}
                      allowDecimals={false}
                      tickFormatter={(v) => {
                        const n = Number(v)
                        if (!Number.isFinite(n)) return ''
                        if (n < 10000) return `${Math.round(n).toLocaleString('fr-CH')}`
                        return `${(n / 1000).toFixed(1)}k`
                      }}
                      label={{ value: 'CHF', angle: -90, position: 'insideLeft', fill: 'rgba(226,232,240,0.32)', fontSize: 10 }}
                    />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ScatterTooltip />} />
                    {Number.isFinite(Number(c.prix_median_concurrent)) ? (
                      <ReferenceLine y={Number(c.prix_median_concurrent)} stroke="rgba(0,30,80,0.55)" strokeDasharray="4 4" />
                    ) : null}
                    <Scatter
                      name="Concurrents même année"
                      data={concurrentsMemeAnnee}
                      fill="rgba(252,252,255,0.94)"
                      stroke="rgba(255,255,255,0.98)"
                      strokeWidth={1.2}
                      shape={(props) => <ScatterPointShape {...props} r={5} />}
                    />
                    <Scatter
                      name="Concurrents autre année"
                      data={concurrentsAutreAnnee}
                      fill="rgba(226,232,240,0.12)"
                      stroke="rgba(226,232,240,0.34)"
                      strokeWidth={1}
                      shape={(props) => <ScatterPointShape {...props} r={5} />}
                    />
                    {yourPoint ? (
                      <Scatter
                        name="Votre annonce"
                        data={[{ ...yourPoint, isYou: true }]}
                        fill="#001E50"
                        stroke="#7ea6ff"
                        strokeWidth={2}
                        shape={(props) => <ScatterPointShape {...props} r={6} />}
                      />
                    ) : null}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : null}

          <div style={{ height: 16 }} />
        </div>

        <div
          style={{
            flexShrink: 0,
            padding: '12px 18px 16px',
            borderTop: '1px solid rgba(148,163,184,0.18)',
            background: 'rgba(15,17,21,0.96)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {!isIncomplete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (!coterStock?.ok) return
                onCoterMoto?.({
                  id_source: mm.id_source,
                  make: mm.make || 'yamaha',
                  model: mm.model,
                  annee: coterStock.annee,
                  km: coterStock.km,
                  titre: mm.titre,
                  version: mm.version,
                })
              }}
              disabled={!coterStock?.ok}
              style={{
                width: '100%',
                padding: '11px 14px',
                borderRadius: RADIUS_BLOCK,
                border: `1px solid ${VW_BLUE}`,
                background: VW_BLUE,
                color: '#f8fafc',
                fontSize: 13,
                fontWeight: 600,
                cursor: !coterStock?.ok ? 'not-allowed' : 'pointer',
                opacity: !coterStock?.ok ? 0.45 : 1,
                fontFamily: 'inherit',
              }}
            >
              Coter ce véhicule
            </button>
          )}
          {!isIncomplete && !coterStock?.ok ? (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
              Cotation : année ou kilométrage insuffisamment fiables (vérifiez l&apos;annonce ou complétez les champs en base).
            </div>
          ) : null}
          {mm.url ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onOpenMonAnnonce?.(mm.url)
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: RADIUS_BLOCK,
                border: '1px solid rgba(148,163,184,0.28)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Ouvrir mon annonce
            </button>
          ) : null}
          {ficheSeller != null && Number.isFinite(Number(ficheSeller)) && c.situation !== 'exclusivite' ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onOpenFicheConcurrent?.({
                  sellerId: Number(ficheSeller),
                  context: {
                    model: mm.model || '',
                    annonce_url: mm.url || null,
                    annonce_titre: mm.titre || '',
                  },
                })
              }}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: RADIUS_BLOCK,
                border: '1px solid var(--border)',
                background: 'rgba(255,255,255,0.04)',
                color: 'var(--text)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Fiche revendeur
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
